import moment from "moment";
import { useContext, useEffect, useMemo } from "react";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { Button, ErrorState } from "@rio-cloud/rio-uikit";
import SubNavigation from "@rio-cloud/rio-uikit/SubNavigation";
import { CheckpointStop, DriverTripDetailsResponse, SocialUser } from "@types";
import { formatDateUTCBr, formatLocalDate, formatTimeBr, formatDateBrUtc, formatStopArrivalLabel, formatStopArrivalUtcToLocal } from "@utils";
import Chip from "../Chip";
import { IStop } from "../RegisterRoute";
import TableTripsDetails from "../TableTripDetails";
import Avatar from "../Avatar/indext";
import TableChip from "../TableChip";
import { AuthContext } from "src/providers/AuthProvider";

interface Props {
  tripDetails?: DriverTripDetailsResponse | null;
  driverId?: string;
  followers?: Array<SocialUser>;
  usersAmity?: Amity.User[];
  type?: "PUBLIC" | "DEFAULT";
  onClose?: () => void;
  currencyAddress?: string;
  currencyLocation?: string;
  handleStopItem?: (checkpointStop?: CheckpointStop) => void;
}

const Label = ({ children }: { children: React.ReactNode }) => {
  return <span className="text-size-14 text-normal line-height-18 text-color-darkest">{children}</span>;
};

const Value = ({ children }: { children: React.ReactNode }) => {
  return <span className="text-size-16 text-normal line-height-20 text-color-darkest">{children}</span>;
};

const Input = ({
  label,
  value,
  iconLeft,
  disabled = true,
  onClickButton,
}: {
  label: string;
  value: string;
  iconLeft: string;
  disabled?: boolean;
  onClickButton?: () => void;
}) => {
  return (
    <div className="display-flex align-items-center margin-top-10 margin-bottom-10">
      <div className="width-100pct">
        <label htmlFor="input5">{label}</label>
        <div className="input-group gap-15">
          <span className="input-group-addon">
            <span className={iconLeft} />
          </span>
          <input id="input5" className="form-control" type="text" placeholder="" disabled value={value} />
          <Button
            bsStyle={Button.PRIMARY}
            iconOnly
            iconName="rioglyph rioglyph-checkboxes"
            disabled={disabled}
            onClick={onClickButton}
          />
        </div>
      </div>
    </div>
  );
};

const SidebarTripDetail = ({
  tripDetails,
  driverId,
  followers,
  usersAmity,
  onClose,
  type = "DEFAULT",
  currencyAddress,
  handleStopItem,
}: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "1";

  const partida = tripDetails?.routes?.startDateTime
    ? formatLocalDate(tripDetails?.routes?.startDateTime, "DD/MM/YYYY [às] HH:mm")
    : formatLocalDate(tripDetails?.routes.startDateTimePlanned, "DD/MM/YYYY [às] HH:mm");

  const chegada = formatLocalDate(tripDetails?.routes?.endDateTime, "DD/MM/YYYY [às] HH:mm");

  const userData = useMemo(() => {
    return usersAmity?.find((res) => res.userId === tripDetails?.driverId);
  }, [usersAmity, driverId]);

  const isFollower = useMemo(() => {
    return !!tripDetails?.driverId && followers?.some((res) => res.from === tripDetails.driverId);
  }, [followers, tripDetails?.driverId]);


  const handleTabChange = (tabKey: string) => {
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set("tab", tabKey);

    navigate(
      {
        pathname: location.pathname,
        search: newSearchParams.toString(),
      },
      {
        state: location.state,
      }
    );
  };

  useEffect(() => {
    if (!searchParams.get("tab")) {
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.set("tab", "1");

      navigate(
        {
          pathname: location.pathname,
          search: newSearchParams.toString(),
        },
        { state: location.state }
      );
    }
  }, []);


  const metrics = tripDetails?.performance
    ? [
      {
        parameter: tripDetails.performance.emitionCO2.name,
        planned: tripDetails.performance.emitionCO2.planned,
        accomplished: tripDetails.performance.emitionCO2.realized,
        icon: "rioglyph rioglyph-globe",
        status: "success",
      },
      {
        parameter: tripDetails.performance.fuelConsumption.name,
        planned: tripDetails.performance.fuelConsumption.planned,
        accomplished: tripDetails.performance.fuelConsumption.realized,
        icon: "rioglyph rioglyph-filling-station",
        status: "success",
      },
      {
        parameter: tripDetails.performance.fuelEfficiency.name,
        planned: tripDetails.performance.fuelEfficiency.planned,
        accomplished: tripDetails.performance.fuelEfficiency.realized,
        icon: "rioglyph rioglyph-filling-station",
        status: "neutral",
      },
      {
        parameter: tripDetails.performance.distance.name,
        planned: tripDetails.performance.distance.planned,
        accomplished: tripDetails.performance.distance.realized,
        icon: "rioglyph rioglyph-road",
        status: "neutral",
      },
      {
        parameter: tripDetails.performance.routeDeviation.name,
        planned: tripDetails.performance.routeDeviation.planned,
        accomplished: tripDetails.performance.routeDeviation.realized,
        icon: "rioglyph rioglyph-road-restrictions",
        status: "success",
      },
      {
        parameter: tripDetails.performance.totalTime.name,
        planned: tripDetails.performance.totalTime.planned,
        accomplished: tripDetails.performance.totalTime.realized,
        icon: "rioglyph rioglyph-time",
        status: "error",
      },
      {
        parameter: tripDetails.performance.idleTime.name,
        planned: tripDetails.performance.idleTime.planned,
        accomplished: tripDetails.performance.idleTime.realized,
        icon: "rioglyph rioglyph-time",
        status: "error",
      },
    ]
    : [];

  const mappedAlerts =
    tripDetails?.alertsInfractions?.map((item) => ({
      infraction: item.name,
      time: formatDateBrUtc(item.time) || '--',
      icon:
        item.type === "EXCESS_SPEED"
          ? "rioglyph rioglyph-kickdown"
          : item.type === "HARSH_BREAK"
            ? "rioglyph rioglyph-brake"
            : "rioglyph rioglyph-warning-sign",
    })) ?? [];

  const formatDeviationPlace = (
    address?: string | null,
    latitude?: number | null,
    longitude?: number | null,
  ): string | null => {
    if (address) return address;
    if (latitude != null && longitude != null) return `${latitude}, ${longitude}`;
    return null;
  };

  const mappedDeviations =
    tripDetails?.desviation?.map((item) => ({
      driverName: tripDetails.driver,
      startDate: formatLocalDate(item.startDate, "DD/MM/YYYY HH:mm"),
      endDate: formatLocalDate(item.endDate, "DD/MM/YYYY HH:mm"),
      destiny: item.destiny,
      exitPlace: formatDeviationPlace(item.exitAddress, item.exitLatitude, item.exitLongitude),
      returnPlace: formatDeviationPlace(item.returnAddress, item.returnLatitude, item.returnLongitude),
    })) ?? [];

  type RawStop = { address?: string; label?: string; type?: string; stop?: IStop; stayTime?: string, checkpoint?: CheckpointStop, estimatedArrival?: string | null, actualArrival?: string | null, plannedArrival?: string | null };

  const formatStayTime = (stayTime: string): string => {
    const hours = moment(stayTime).hours()
    const minutes = moment(stayTime).minutes()

    const hoursFormatted = !hours ? '' : hours === 1 ? `${hours} hora` : `${hours} horas`;
    const minutesFormatted = !minutes ? '' : minutes === 1 ? `${minutes} minuto` : `${minutes} minutos`;

    const auxiliaryText = hoursFormatted && minutesFormatted ? 'e' : '';

    return [hoursFormatted, auxiliaryText, minutesFormatted].filter(Boolean).join(' ');
  };

  const buildStopArrival = (stop: RawStop): string => {
    if (stop?.actualArrival) return `Chegada em ${formatStopArrivalLabel(formatStopArrivalUtcToLocal(stop.actualArrival) ?? stop.actualArrival)}`;
    if (stop?.estimatedArrival) return `Previsão de chegada ${formatStopArrivalLabel(formatStopArrivalUtcToLocal(stop.estimatedArrival) ?? stop.estimatedArrival)}`;
    if (stop?.plannedArrival) return `Chegada planejada ${formatStopArrivalLabel(stop.plannedArrival)}`;

    return "";
  };

  const mappedRouteDetails = {
    origin: tripDetails?.routes?.originName || "",
    destination: tripDetails?.routes?.destinyName || "",
    stops: ((tripDetails?.routes?.stops as RawStop[]) || []).map((stop) => {
      const stopNote = stop?.stop?.note || stop?.stop?.name || stop?.address || "Parada";
      const isUnplanned = stop?.type === "UNPLANNED";
      const noteLabel = isUnplanned ? `${stopNote} (Não programada)` : stopNote;
      return {
        note: [noteLabel, stop?.stayTime ? formatStayTime(stop.stayTime) : "", buildStopArrival(stop)]
          .filter(Boolean)
          .join(" - "),
        label: stop.stop?.position?.address?.label || stop?.address || "",
        checkpoint: stop.checkpoint,
        isUnplanned,
      };
    }),
  };

  const renderTabContent = ({ handleStopItem }: { handleStopItem?: (checkpoint?: CheckpointStop) => void; }) => {
    switch (activeTab) {
      case "1":
        return (
          <div className="padding-15">
            <TableTripsDetails
              viewType="TABLE"
              pages={1}
              page={1}
              metrics={metrics}
              handlePage={() => { }}
              handleSort={() => { }}
              handleShowTripDetail={() => { }}
            />
          </div>
        );
      case "2":
        return (
          <div className="padding-15">
            <div className="padding-y-10 padding-x-15">
              <Input
                label={`Origem (Partida em ${partida})`}
                value={mappedRouteDetails.origin}
                iconLeft="rioglyph rioglyph-start"
              />

              {mappedRouteDetails.stops.length > 0 && (
                <div className="padding-left-20">
                  {mappedRouteDetails.stops.map((s, i) => (
                    <Input
                      key={i}
                      label={s.note}
                      value={s.label}
                      iconLeft="rioglyph rioglyph-pushpin"
                      disabled={s.isUnplanned || !s?.checkpoint?.id}
                      onClickButton={() => {
                        if (s.checkpoint) {
                          handleStopItem?.({
                            ...s.checkpoint,
                          });
                        }
                      }}
                    />
                  ))}
                </div>
              )}

              {tripDetails?.routes?.endDateTime ? (
                <Input
                  label={`Destino (Chegada em ${chegada})`}
                  value={mappedRouteDetails.destination}
                  iconLeft="rioglyph rioglyph-finish"
                />
              ) : (
                <Input
                  label="Destino (Viagem cancelada)"
                  value={mappedRouteDetails.destination}
                  iconLeft="rioglyph rioglyph-finish"
                />
              )}
            </div>
          </div>
        );
      case "3":
        return (
          <>
            {mappedAlerts?.length > 0 ? mappedAlerts?.map((item, index) => (
              <div
                key={index}
                className="
            container fluid-small padding-15
            last-child-margin-bottom-0
            display-flex justify-content-between align-items-center
          "
                style={{ borderBottom: "1px solid var(--gray-light)" }}
              >
                <div>
                  <span className={`${item.icon} text-color-gray text-size-16 margin-right-20`}></span>
                  <span className="text-color-darker text-size-14">{item.infraction}</span>
                </div>
                <div>
                  <span className="text-color-darker text-size-14">{item.time}</span>
                </div>
              </div>
            )) : (
              <ErrorState
                headline='Resultado'
                message={'Nenhum registro de alertas ou infracões para essa viagem.'}
                icon="rioglyph rioglyph-info"
                condensed
                innerClassName="border-none"
                outerClassName="border-none"
              />
            )}
          </>
        );
      case "4":
        return (
          <>
            {mappedDeviations?.length > 0 ? mappedDeviations?.map((item, index) => (
              <div
                key={index}
                className="
            container fluid-small padding-15
            last-child-margin-bottom-0
            display-flex justify-content-between align-items-center
          "
                style={{ borderBottom: "1px solid var(--gray-light)" }}
              >
                <div className="display-flex align-items-center">
                  <div
                    className="bg-danger width-40 height-40 display-flex align-items-center justify-content-center rounded-circle padding-bottom-5 margin-right-10">
                    <span className="rioglyph rioglyph-warning-sign text-size-h4 always-text-color-white" />
                  </div>
                  <div className="display-flex flex-column">
                    <span
                      className="text-capitalize text-color-black text-size-16 margin-right-20">{item.driverName}</span>
                    <div>
                      <span className="rioglyph rioglyph-calendar text-color-gray text-size-16 margin-right-3"></span>
                      {item.startDate?.toString()} Até {item.endDate?.toString()}
                    </div>
                    <div>
                      <span className="rioglyph rioglyph-map-marker text-color-gray text-size-16 margin-right-4"></span>
                      <span className="text-color-darker text-size-14">
                        Saiu: {item.exitPlace ?? "local não informado"}
                      </span>
                    </div>
                    <div>
                      <span className="rioglyph rioglyph-finish text-color-gray text-size-16 margin-right-4"></span>
                      <span className="text-color-darker text-size-14">
                        {item.returnPlace ? `Voltou: ${item.returnPlace}` : "Não retornou à rota"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )) : (
              <ErrorState
                headline='Resultado'
                message={'Nenhum registro de desvio de rota para essa viagem.'}
                icon="rioglyph rioglyph-info"
                condensed
                innerClassName="border-none"
                outerClassName="border-none"
              />
            )}
          </>
        );
      default:
        return null;
    }
  };

  const { getDetailUser} = useContext(AuthContext);
  const user_amity = getDetailUser?.(tripDetails?.profileId);

  return (
    <div>
      {type !== "PUBLIC" ? (
        <div className="display-flex flex-direction-row align-items-center padding-x-15">
          <Avatar width="width-50" url={user_amity?.metadata?.imageProfile?.fileUrl} />
          <div className="padding-x-15">
            <span className="text-bold text-size-18 text-capitalize line-height-20">{tripDetails?.driver}</span>
            <div className="margin-top-5">
              <TableChip
                disabledDriver={!tripDetails?.driverId}
                disabledProfile={!tripDetails?.profileId}
                user={user_amity}
              />
            </div>
            <div className="flex margin-top-5">
              <span className="rioglyph rioglyph-truck"></span>
              <span className="margin-left-5">{tripDetails?.asset}</span>
            </div>
          </div>
        </div>
      ) : null}

      {type === "PUBLIC" ? (
        <div className="display-flex align-items-center padding-y-20" style={{ marginLeft: 6, marginBottom: 4 }}>
          <span className="rioglyph rioglyph-truck text-size-h1"></span>
          <span className="text-bold text-size-18">{tripDetails?.asset}</span>
        </div>
      ) : null}

      {type === "PUBLIC" ? (
        <>
          <div className="padding-y-16 padding-x-15">
            <span>Ultima Posição identificada</span>
            <br />
            <span className="text-normal text-size-16">{currencyAddress}</span>
          </div>

          <div className="display-flex padding-y-15 padding-x-15">
            <div className="display-flex align-items-center gap-4">
              <span className="rioglyph rioglyph-map-marker text-size-18 text-color-primary"></span>
              <span className="text-normal text-size-16">
                {/*{detail?.latLongActually?.lat}, {detail?.latLongActually?.lng}*/}
              </span>
            </div>
          </div>

          <div className="display-flex padding-y-15 padding-x-15">
            <div className="width-50pct">
              <span>Horário de saida</span>
              <br />
              <span className="text-normal text-size-16">{formatDateUTCBr("")}</span>
            </div>
            <div className="width-50pct">
              <span>Previsão de chegada</span>
              <br />
              <span className="text-normal text-size-16">{formatDateUTCBr("")}</span>
            </div>
          </div>
        </>
      ) : null}
      <div className="margin-top-10">
        <div
          className="bg-white border-bottom-only border-width-1 border-style-solid border-color-light"
        >
          <SubNavigation
            className="padding-0 margin-0"
            navItems={[
              {
                key: '1',
                route: (
                  <NavLink
                    to="?tab=1"
                    onClick={(e) => {
                      e.preventDefault();
                      handleTabChange("1");
                    }}
                    className={({ isActive }) => (searchParams.get("tab") === "1" ? "active" : "")}
                  >
                    DESEMPENHO
                  </NavLink>
                ),
              },
              {
                key: "2",
                route: (
                  <NavLink
                    to="?tab=1"
                    onClick={(e) => {
                      e.preventDefault();
                      handleTabChange("2");
                    }}
                    className={({ isActive }) => (searchParams.get("tab") === "2" ? "active" : "")}
                  >
                    ROTA
                  </NavLink>
                ),
              },
              {
                key: '3',
                route: (
                  <NavLink
                    to="?tab=1"
                    onClick={(e) => {
                      e.preventDefault();
                      handleTabChange("3");
                    }}
                    className={({ isActive }) => (searchParams.get("tab") === "3" ? "active" : "")}
                  >
                    ALERTAS & INFRAÇÕES
                  </NavLink>
                ),
              },
              {
                key: "4",
                route: (
                  <NavLink
                    to="?tab=1"
                    onClick={(e) => {
                      e.preventDefault();
                      handleTabChange("4");
                    }}
                    className={({ isActive }) => (searchParams.get("tab") === "4" ? "active border-none" : "")}
                  >
                    DESVIOS DE ROTA
                  </NavLink>
                ),
              },
            ]}
          />
        </div>
      </div>

      {renderTabContent({
        handleStopItem,
      })}
    </div>
  );
};

export default SidebarTripDetail;

